<script lang="ts">
// Module lifetime prevents ID reuse when a generation remounts a division panel.
let nextPoId = 0;
</script>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import AppPopover from "../../components/AppPopover.vue";
import DatePicker from "../../components/DatePicker.vue";
import Dropdown from "../../components/Dropdown.vue";
import FormField from "../../components/FormField.vue";
import LucideIcon from "../../components/icons/LucideIcon.vue";
import OKCancelModal from "../../components/OKCancelModal.vue";
import StatusBanner from "../../components/StatusBanner.vue";
import Textbox from "../../components/Textbox.vue";
import type { DropdownOption } from "../../redistribution/models/DropdownOption.ts";
import {
  useModalStore,
  type ModalContent,
  type ModalOptions,
} from "../../stores/modalStore.ts";
import { formatCalendarDateForDisplay } from "../../utils/dateFormatting.ts";
import type { BulkOrderCreationRow } from "./bulkOrderCreationMock.ts";
import CapacityWarningModal from "./CapacityWarningModal.vue";
import {
  getDeliverToOptions,
  resolveCurrentDeliverTo,
} from "./deliverToOptions.ts";
import LeftoverInventoryMoveModal from "./LeftoverInventoryMoveModal.vue";
import LeftoverInventorySidebar from "./LeftoverInventorySidebar.vue";
import NewPoTransferModal from "./NewPoTransferModal.vue";
import PoChangesModal from "./PoChangesModal.vue";
import QuantityDispositionModal from "./QuantityDispositionModal.vue";
import QuantityIncreaseConfirmModal from "./QuantityIncreaseConfirmModal.vue";
import {
  committedPoChanges,
  snapshotPos,
  type PoChangeSnapshot,
  type PoTransfer,
} from "./shippingPoChanges.ts";
import {
  addSkuLineToPo,
  applyBatchDispositionPlan,
  applyCapacityAwareRedistribution,
  applyPriceInput,
  applyQuantityIncreasePlan,
  buildBlankShippingPo,
  buildDispositionCandidates,
  buildEditableShippingPos,
  buildProjectedPoAfterMove,
  computeHeaderTotalCostUsd,
  computeLiveLeftoverRows,
  computePoTotals,
  computeSourceQtyBySku,
  formatCurrencyUsd,
  formatLineCapacityPercent,
  getAvailableCapacityFt3,
  getMaxQuantityWithinCapacity,
  getUtilizationStatus,
  parseWholeQuantityInput,
  planBatchDisposition,
  planCapacityAwareRedistribution,
  planProportionalLeftoverMove,
  planQuantityIncrease,
  type BatchDispositionPlan,
  type BatchDispositionResult,
  type CapacityWarningChoice,
  type EditableShippingPo,
  type EditableShippingPoLine,
  type LeftoverInventoryMoveApplyRequest,
  type LeftoverInventoryMoveApplyResult,
  type LeftoverInventoryMoveDraft,
  type LeftoverInventoryMoveSnapshot,
  type LeftoverMoveMode,
  type QuantityIncreasePlan,
  type ReleasedLine,
  type ShippingPoTotals,
} from "./shippingPoEditing.ts";
import type {
  ShippingPoGenerationResult,
  SortDirection,
  SuggestedPoSortField,
} from "./shippingPoGeneration.ts";

const props = withDefaults(
  defineProps<{
    rows: BulkOrderCreationRow[];
    generationResult: ShippingPoGenerationResult;
    cubeCapacityFt3: number;
    /** "rail" restores the compact, horizontally-scrolling card layout. */
    layout?: "stack" | "rail" | "grid";
    showLayoutToggle?: boolean;
    /** Warehouse options used for the per-PO Deliver To field (filtered to the PO's DC). */
    warehouseOptions?: DropdownOption[];
    division?: string;
    demoMode?: boolean;
    idPrefix?: string;
    optimized?: boolean;
    sortField?: SuggestedPoSortField;
    sortDirection?: SortDirection;
    nextPoSequence?: () => number;
  }>(),
  {
    layout: "stack",
    showLayoutToggle: true,
    warehouseOptions: () => [],
    demoMode: false,
    sortField: "poNumber",
    sortDirection: "ascending",
  }
);

const emit = defineEmits<(event: "update:hasEdits", value: boolean) => void>();
function scopedId(id: string): string {
  return props.idPrefix ? `${props.idPrefix}-${id}` : id;
}
const modalStore = useModalStore();
const leftoverMoveDialogId = computed(() =>
  scopedId("leftover-inventory-move")
);
const leftoverMoveModalOpen = computed(() =>
  modalStore.isOpen(leftoverMoveDialogId.value)
);
const leftoverInventoryOpen = ref(false);
const leftoverSelectedSkus = ref<string[]>([]);
const leftoverMoveDraft = ref<LeftoverInventoryMoveDraft>();
const gridLayoutEnabled = ref(false);
const activeLayout = computed(() =>
  gridLayoutEnabled.value ? "grid" : props.layout
);

/** Hidden until the PurchaseOrder/calculate integration provides a Needed By source. */
const showNeededByColumn = false;

const editablePos = ref<EditableShippingPo[]>([]);
const newLeftoverTarget = ref<EditableShippingPo>();
const selectedLinesByPo = ref<Record<string, string[]>>({});
const committedChanges = ref<Record<string, string[]>>({});
let editSnapshot: PoChangeSnapshot[] = [];
let pendingTransfers: PoTransfer[] = [];

function recordChanges(
  before: PoChangeSnapshot[],
  transfers: PoTransfer[]
): void {
  const changes = committedPoChanges(
    before,
    snapshotPos(editablePos.value),
    transfers
  );
  for (const [id, entries] of Object.entries(changes)) {
    if (entries.length > 0) {
      committedChanges.value[id] = [
        ...(committedChanges.value[id] ?? []),
        ...entries,
      ];
    }
  }
}

function showPoChanges(po: EditableShippingPo): void {
  showPanelModal(PoChangesModal, {
    dialogId: scopedId(`changes-${po.id}`),
    width: Math.min(640, Math.max(320, window.innerWidth - 32)),
    height: Math.min(520, Math.max(240, window.innerHeight - 32)),
    componentProps: {
      poNumber: po.sequence,
      changes: [...(committedChanges.value[po.id] ?? [])],
    },
  });
}

function capacityDescription(po: EditableShippingPo): string {
  const available = getAvailableCapacityFt3(po);
  if (available === undefined) return "Capacity unavailable";
  if (available < 0) {
    const overage = Math.round(-available * 10) / 10;
    return `${overage === 0 ? "<0.1" : String(overage)} ft³ over capacity`;
  }
  return `${String(poTotals(po).remainingCapacityFt3)} ft³ available`;
}

function allocatePoId(): string {
  return scopedId(`local-shipping-po-${String(++nextPoId)}`);
}

function blankPo(division?: string): EditableShippingPo {
  const sequence =
    props.nextPoSequence?.() ??
    Math.max(0, ...editablePos.value.map((po) => po.sequence)) + 1;
  return buildBlankShippingPo(
    allocatePoId(),
    sequence,
    props.cubeCapacityFt3,
    division
  );
}

function addBlankPo(): void {
  if (
    activeEditId.value ||
    decisionPending.value ||
    leftoverMoveModalOpen.value
  )
    return;
  editablePos.value.push(blankPo(props.division ?? props.rows[0]?.division));
}

function newPoTransferPlan(
  target: EditableShippingPo,
  lines: EditableShippingPoLine[]
) {
  return planProportionalLeftoverMove(
    target,
    lines.map((line) => ({
      ...line,
      requestedUnits: line.qty,
      cubicFeet: line.qty * line.cubicFeetPerUnit,
      shipDate: "",
      shipToWarehouse: "",
      status: "Ready" as const,
    })),
    Object.fromEntries(lines.map((line) => [line.sku, line.qty]))
  );
}

function requestNewPoTransfer(
  source: EditableShippingPo,
  lines: EditableShippingPoLine[],
  onApplied: (quantities: Record<string, number>) => void,
  onCancel: () => void
): void {
  const target = blankPo(source.division);
  decisionPending.value = true;
  showPanelModal(NewPoTransferModal, {
    width: Math.min(560, Math.max(320, window.innerWidth - 32)),
    height: Math.min(480, Math.max(240, window.innerHeight - 32)),
    dialogId: scopedId(`new-po-transfer-${source.id}`),
    componentProps: {
      lines,
      cubeCapacityFt3: props.cubeCapacityFt3,
      onConfirm: (mode: LeftoverMoveMode) => {
        const plan = newPoTransferPlan(target, lines);
        if (mode === "fit" && !plan.capacityMeasurable) {
          onCancel();
          return;
        }
        const quantities =
          mode === "override"
            ? Object.fromEntries(lines.map((line) => [line.sku, line.qty]))
            : plan.quantitiesBySku;
        target.lines = lines.flatMap((line) => {
          const qty = quantities[line.sku] ?? 0;
          return qty > 0
            ? [{ ...line, qty, qtyError: "", priceError: "" }]
            : [];
        });
        if (target.lines.length > 0) {
          editablePos.value.push(target);
          showMoveSuccess(
            target,
            target.lines.map((line) => ({
              sku: line.sku,
              quantity: line.qty,
              source: `PO ${String(poDisplayNumber(source))}`,
            }))
          );
        }
        onApplied(quantities);
      },
      onCancel,
    },
  });
}

function moveSelectedLines(po: EditableShippingPo): void {
  if (
    activeEditId.value !== po.id ||
    decisionPending.value ||
    leftoverMoveModalOpen.value
  )
    return;
  const selected = new Set(selectedLinesByPo.value[po.id]);
  const lines = po.lines.filter(
    (line) => selected.has(line.sku) && line.qty > 0
  );
  if (lines.length === 0) return;
  requestNewPoTransfer(
    po,
    lines.map((line) => ({ ...line })),
    (quantities) => {
      for (const line of lines) line.qty -= quantities[line.sku] ?? 0;
      po.lines = po.lines.filter(
        (line) => !lines.includes(line) || line.qty > 0
      );
      selectedLinesByPo.value[po.id] = [];
    },
    () => {
      // Cancellation leaves the source and target list untouched.
    }
  );
}

function selectLine(
  po: EditableShippingPo,
  sku: string,
  checked: boolean
): void {
  if (activeEditId.value !== po.id || decisionPending.value) return;
  const selected = new Set(selectedLinesByPo.value[po.id]);
  if (checked) selected.add(sku);
  else selected.delete(sku);
  selectedLinesByPo.value[po.id] = [...selected];
}
const editingPoIds = ref<Record<string, boolean>>({});
const activeEditId = ref<string>();
const saving = ref(false);
const decisionPending = ref(false);
let mutationEpoch = 0;
const ownedDialogs = new Set<string>();
const pendingSaves = new Set<(accepted: boolean) => void>();
let restoreSession: (() => void) | undefined;
let deliverToDefaultsPending = false;

/** Every callback belongs to the generation/session that opened its dialog. */
function showPanelModal(content: ModalContent, options: ModalOptions): void {
  const epoch = mutationEpoch;
  const dialogId = options.dialogId;
  if (dialogId) ownedDialogs.add(dialogId);
  let resolved = false;
  const guardDecision =
    (callback: (...args: unknown[]) => unknown) =>
    (...args: unknown[]) => {
      if (epoch !== mutationEpoch || resolved) return;
      resolved = true;
      decisionPending.value = false;
      return callback(...args);
    };
  const componentProps = { ...options.componentProps };
  for (const name of ["onConfirm", "onCancel", "onOk"]) {
    const callback = componentProps[name];
    if (typeof callback === "function") {
      componentProps[name] = guardDecision(
        callback as (...args: unknown[]) => unknown
      );
    }
  }
  const onCancel = componentProps.onCancel as (() => void) | undefined;
  modalStore.show(content, {
    ...options,
    componentProps,
    onClose: () => {
      if (epoch !== mutationEpoch || resolved) return;
      if (onCancel) onCancel();
      else {
        resolved = true;
        decisionPending.value = false;
        void options.onClose?.();
      }
    },
  });
}

function invalidatePendingMutations(): void {
  mutationEpoch++;
  for (const id of ownedDialogs) modalStore.closeById(id);
  ownedDialogs.clear();
  for (const settle of pendingSaves) settle(false);
  pendingSaves.clear();
  decisionPending.value = false;
  saving.value = false;
}

function canMutateLine(line: EditableShippingPoLine): boolean {
  const po = findPoForLine(line);
  return Boolean(po && po.id === activeEditId.value);
}
const priceDrafts = ref(
  new Map<EditableShippingPoLine, { value: string; needsCommit: boolean }>()
);
const qtyDrafts = ref(
  new Map<EditableShippingPoLine, { value: string; needsCommit: boolean }>()
);
const poBaselineSignatures = ref(new Map<string, string>());
const leftoverMoveNotices = ref<Record<string, string>>({});
const leftoverMoveHighlightedSkusByPo = ref<Record<string, string[]>>({});

function getPoSignature(po: EditableShippingPo): string {
  return JSON.stringify({
    deliverTo: po.deliverTo,
    shipDate: po.shipDateValue?.getTime() ?? null,
    arrivalDate: po.arrivalDate?.getTime() ?? null,
    lines: po.lines.map((line) => ({
      sku: line.sku,
      qty: qtyDrafts.value.get(line)?.value ?? String(line.qty),
      neededBy: line.neededBy?.getTime() ?? null,
      unitPrice:
        priceDrafts.value.get(line)?.value ??
        formatCurrencyUsd(line.unitPriceUsd),
    })),
  });
}

watch(
  () =>
    editablePos.value.length !== poBaselineSignatures.value.size ||
    editablePos.value.some(
      (po) => getPoSignature(po) !== poBaselineSignatures.value.get(po.id)
    ),
  (hasEdits) => {
    emit("update:hasEdits", hasEdits);
  },
  { flush: "post" }
);

/** Current Deliver To option per PO, resolved from the PO's generated lines. */
const currentDeliverToByPo = ref(new Map<string, DropdownOption | undefined>());
/** Pending "Add SKU to PO" picker values per PO; one entry per picker row. */
const addSkuDrafts = ref<Record<string, (string | undefined)[]>>({});

function resolveDeliverToDefaults(): void {
  // Late option loading must not compete with a cancellable edit journal.
  if (activeEditId.value) {
    deliverToDefaultsPending = true;
    return;
  }
  deliverToDefaultsPending = false;
  for (const po of editablePos.value) {
    if (po.deliverTo !== "" || po.userCreated) continue;
    const wasUpdated = isPoUpdated(po);
    const sourceRows =
      props.generationResult.suggestedPos.find(
        (suggested) => suggested.sequence === po.sequence
      )?.rows ?? [];
    const current = resolveCurrentDeliverTo(sourceRows, props.warehouseOptions);
    currentDeliverToByPo.value.set(po.id, current);
    po.deliverTo = current?.value ?? "";
    if (!wasUpdated) poBaselineSignatures.value.set(po.id, getPoSignature(po));
  }
}

watch(
  () => [props.generationResult, props.cubeCapacityFt3] as const,
  ([result, capacity]) => {
    invalidatePendingMutations();
    activeEditId.value = undefined;
    editingPoIds.value = {};
    restoreSession = undefined;
    committedChanges.value = {};
    editSnapshot = [];
    pendingTransfers = [];
    editablePos.value = buildEditableShippingPos(result, capacity);
    for (const po of editablePos.value) po.id = allocatePoId();
    sortPos();
    newLeftoverTarget.value = undefined;
    selectedLinesByPo.value = {};
    priceDrafts.value.clear();
    qtyDrafts.value.clear();
    currentDeliverToByPo.value.clear();
    addSkuDrafts.value = {};
    leftoverMoveDraft.value = undefined;
    leftoverSelectedSkus.value = [];
    leftoverMoveNotices.value = {};
    leftoverMoveHighlightedSkusByPo.value = {};
    poBaselineSignatures.value = new Map(
      editablePos.value.map((po) => [po.id, getPoSignature(po)])
    );
    resolveDeliverToDefaults();
  },
  { immediate: true }
);

watch(
  () => [props.sortField, props.sortDirection] as const,
  () => {
    sortPos();
  }
);

// Warehouse options can load after generation; fill defaults without wiping edits.
watch(
  () => props.warehouseOptions,
  () => {
    resolveDeliverToDefaults();
  }
);

const sourceQtyBySku = computed(() => computeSourceQtyBySku(props.rows));
const headerTotalCostUsd = computed(() =>
  computeHeaderTotalCostUsd(editablePos.value)
);
/**
 * Live leftover inventory (BUG-6): derived from current PO allocations on
 * every render, not a one-time generation snapshot, so it reflects SKUs
 * added to a PO, quantity edits, and "move to leftover" dispositions alike.
 */
const liveLeftoverRows = computed(() =>
  computeLiveLeftoverRows(editablePos.value, props.rows)
);
const leftoverInventoryButtonLabel = computed(() => {
  const count = liveLeftoverRows.value.length;
  return `${String(count)} item${count === 1 ? "" : "s"} in leftover inventory`;
});

const FLAG_PO_AS_OPTIONS: DropdownOption[] = [
  { label: "CNY", value: "CNY" },
  { label: "MGR", value: "MGR" },
];
const TERMS_OPTIONS: DropdownOption[] = [
  { label: "Wire", value: "Wire" },
  { label: "Net", value: "Net" },
];
const SHIP_VIA_OPTIONS: DropdownOption[] = [
  { label: "Truck", value: "Truck" },
  { label: "Ocean", value: "Ocean" },
  { label: "Other", value: "Other" },
];
const flagPoAs = ref<string | undefined>(undefined);
const terms = ref<string | undefined>(undefined);
const shipVia = ref<string | undefined>(undefined);
const shipViaError = ref("");
const actionNotice = ref("");

const NOT_IMPLEMENTED_NOTICE =
  "is not yet available for Suggested Shipping POs \u2014 backend integration is not implemented yet.";

const moreMenuOpen = ref(false);
const moreMenuRef = ref<HTMLElement | undefined>(undefined);
const cardRefs: Record<string, HTMLElement | undefined> = {};

function setCardRef(poId: string, el: Element | null): void {
  cardRefs[poId] = (el as HTMLElement | null) ?? undefined;
}

function poTotals(po: EditableShippingPo): ShippingPoTotals {
  return computePoTotals(po);
}

function sortPos(): void {
  const direction = props.sortDirection === "ascending" ? 1 : -1;
  editablePos.value.sort((left, right) => {
    let comparison = 0;
    switch (props.sortField) {
      case "totalCost": {
        const leftCost = poTotals(left).totalCostUsd;
        const rightCost = poTotals(right).totalCostUsd;
        if (leftCost === null || rightCost === null) {
          if (leftCost !== rightCost) {
            return leftCost === null ? 1 : -1;
          }
        } else {
          comparison = leftCost - rightCost;
        }
        break;
      }
      case "availableCapacity": {
        comparison =
          poTotals(left).remainingCapacityFt3 -
          poTotals(right).remainingCapacityFt3;
        break;
      }
      case "arrivalDate": {
        const leftDate = left.arrivalDate?.getTime();
        const rightDate = right.arrivalDate?.getTime();
        if (leftDate === undefined || rightDate === undefined) {
          if (leftDate !== rightDate) {
            return leftDate === undefined
              ? props.sortDirection === "ascending"
                ? -1
                : 1
              : props.sortDirection === "ascending"
                ? 1
                : -1;
          }
        } else {
          comparison = leftDate - rightDate;
        }
        break;
      }
      case "shipDate": {
        const leftDate = left.shipDateValue?.getTime();
        const rightDate = right.shipDateValue?.getTime();
        if (leftDate === undefined || rightDate === undefined) {
          if (leftDate !== rightDate) {
            return leftDate === undefined
              ? props.sortDirection === "ascending"
                ? -1
                : 1
              : props.sortDirection === "ascending"
                ? 1
                : -1;
          }
        } else {
          comparison = leftDate - rightDate;
        }
        break;
      }
      default: {
        comparison = left.sequence - right.sequence;
        break;
      }
    }
    if (comparison === 0) return left.sequence - right.sequence;
    return comparison * direction;
  });
}

function isPoUpdated(po: EditableShippingPo): boolean {
  return getPoSignature(po) !== poBaselineSignatures.value.get(po.id);
}

function formatNeededByForDisplay(neededBy: Date | undefined): string {
  if (!neededBy) return "";

  const month = String(neededBy.getMonth() + 1).padStart(2, "0");
  const day = String(neededBy.getDate()).padStart(2, "0");
  const year = String(neededBy.getFullYear());
  return `${month}/${day}/${year}`;
}

function onQtyChange(line: EditableShippingPoLine, rawValue: string): void {
  if (!canMutateLine(line) || saving.value || decisionPending.value) return;
  qtyDrafts.value.set(line, { value: rawValue, needsCommit: true });
}

function findPoForLine(
  line: EditableShippingPoLine
): EditableShippingPo | undefined {
  return editablePos.value.find((po) => po.lines.includes(line));
}

function poDisplayNumber(po: EditableShippingPo): number {
  if (!editablePos.value.some((candidate) => candidate.id === po.id)) {
    throw new Error(`Unable to find suggested PO ${po.id} for display.`);
  }
  return po.sequence;
}

function showMoveSuccess(
  target: EditableShippingPo,
  moves: { sku: string; quantity: number; source: string }[]
): void {
  const applied = moves.filter((move) => move.quantity > 0);
  if (applied.length === 0) return;
  pendingTransfers.push(
    ...applied.map((move) => ({
      ...move,
      destination: `PO ${String(target.sequence)}`,
    }))
  );
  const sources = [...new Set(applied.map((move) => move.source))];
  const details = sources
    .map((source) => {
      const entries = applied.filter((move) => move.source === source);
      const skus = entries
        .map(
          ({ sku, quantity }) =>
            `${sku} (${String(quantity)} unit${quantity === 1 ? "" : "s"})`
        )
        .join(", ");
      return `SKU${entries.length === 1 ? "" : "s"} ${skus} from ${source}`;
    })
    .join("; ");
  leftoverMoveNotices.value[target.id] =
    `Successfully moved ${details} to PO ${String(poDisplayNumber(target))}.`;
  leftoverMoveHighlightedSkusByPo.value[target.id] = [
    ...new Set(applied.map((move) => move.sku)),
  ];
}

function sourceRowFor(
  sku: string,
  division: string | undefined
): BulkOrderCreationRow {
  const row = props.rows.find(
    (candidate) =>
      candidate.sku === sku &&
      (candidate.division ?? "").trim().toUpperCase() ===
        (division ?? "").trim().toUpperCase()
  );
  if (!row) {
    throw new Error(
      `Unable to find source row for SKU ${sku} in division ${division ?? "unknown"}.`
    );
  }
  return row;
}

function dispositionWarehouseOptions(division: string): DropdownOption[] {
  const normalizedDivision = division.trim().toUpperCase();
  if (!normalizedDivision) return [];
  return props.warehouseOptions.filter((option) => {
    const metadataDivision = option.metadata?.division;
    const optionDivision =
      typeof metadataDivision === "string"
        ? metadataDivision
        : (option.value.split("_")[0] ?? "");
    return optionDivision.trim().toUpperCase() === normalizedDivision;
  });
}

function toReleasedLine(
  po: EditableShippingPo,
  line: EditableShippingPoLine,
  qty: number
): ReleasedLine {
  const sourceRow = sourceRowFor(line.sku, po.division);
  return {
    sku: line.sku,
    qty,
    cubicFeetPerUnit: line.cubicFeetPerUnit,
    ...(line.pieceCount === undefined ? {} : { pieceCount: line.pieceCount }),
    sourceRow: {
      ...sourceRow,
      ...(line.pieceCount === undefined ? {} : { pieceCount: line.pieceCount }),
      ...(po.division ? { division: po.division } : {}),
    },
  };
}

/**
 * Applies a confirmed UX-17 batch disposition plan, surfacing per-recipient
 * success notices and recording transfers (including remainder units that
 * become leftover inventory).
 */
function applyPlanWithFeedback(
  plan: BatchDispositionPlan,
  sourceLabel: string,
  newPo?: EditableShippingPo
): void {
  applyBatchDispositionPlan(plan);
  if (
    newPo &&
    plan.perTarget.some(
      (target) => target.po.id === newPo.id && target.plannedQty > 0
    )
  ) {
    editablePos.value.push(newPo);
  }
  const movesByPo = new Map<
    EditableShippingPo,
    { sku: string; quantity: number; source: string }[]
  >();
  for (const skuPlan of plan.perSku) {
    for (const allocation of skuPlan.allocations) {
      if (allocation.qty <= 0) continue;
      const moves = movesByPo.get(allocation.po) ?? [];
      moves.push({
        sku: skuPlan.sku,
        quantity: allocation.qty,
        source: sourceLabel,
      });
      movesByPo.set(allocation.po, moves);
    }
    if (skuPlan.leftoverQty > 0) {
      pendingTransfers.push({
        sku: skuPlan.sku,
        quantity: skuPlan.leftoverQty,
        source: sourceLabel,
        destination: "Leftover inventory",
      });
    }
  }
  for (const [target, moves] of movesByPo) showMoveSuccess(target, moves);
}

/**
 * Shows the UX-37 rule-2 confirmation for a quantity increase that needs
 * units taken from other suggested POs (increases covered entirely by
 * leftover inventory apply without this prompt).
 */
function requestQuantityIncreaseConfirmation(
  plan: QuantityIncreasePlan,
  onApplied: () => void,
  onCancelled: () => void
): void {
  const targetPo = findPoForLine(plan.line);
  decisionPending.value = true;
  showPanelModal(QuantityIncreaseConfirmModal, {
    width: 480,
    height: 360,
    dialogId: scopedId(`qty-increase-${plan.line.sku}-${targetPo?.id ?? ""}`),
    componentProps: {
      sku: plan.line.sku,
      targetPoSequence: targetPo ? poDisplayNumber(targetPo) : 0,
      newQty: plan.newQty,
      fromLeftover: plan.fromLeftover,
      reductions: plan.reductions.map((reduction) => ({
        poSequence: poDisplayNumber(reduction.po),
        currentQty: reduction.line.qty,
        newQty: reduction.line.qty - reduction.reduceBy,
      })),
      onConfirm: () => {
        applyQuantityIncreasePlan(plan);
        if (targetPo) {
          showMoveSuccess(targetPo, [
            ...plan.reductions.map((reduction) => ({
              sku: plan.line.sku,
              quantity: reduction.reduceBy,
              source: `PO ${String(poDisplayNumber(reduction.po))}`,
            })),
            {
              sku: plan.line.sku,
              quantity: plan.fromLeftover,
              source: "leftover inventory",
            },
          ]);
        }
        onApplied();
      },
      onCancel: onCancelled,
    },
  });
}

/**
 * Shows the UX-17 batch disposition dialog for units released by a quantity
 * decrease, a SKU line removal, or a whole-PO deletion. On confirmation the
 * plan is recomputed from the user's selections (pure and deterministic) and
 * applied with per-recipient feedback before `onApplied` finalizes the
 * source-side change; cancellation leaves every PO untouched.
 */
function requestBatchDisposition(options: {
  sourcePo: EditableShippingPo;
  releasedLines: ReleasedLine[];
  context: "delete-po" | "reduce-line" | "remove-line";
  dialogId: string;
  sourceLabel: string;
  onApplied: () => void;
  onCancelled: () => void;
}): void {
  const { sourcePo, releasedLines } = options;
  const sourceRows =
    props.generationResult.suggestedPos.find(
      (suggested) => suggested.sequence === sourcePo.sequence
    )?.rows ?? [];
  const sourceWarehouse =
    currentDeliverToByPo.value.get(sourcePo.id) ??
    resolveCurrentDeliverTo(sourceRows, props.warehouseOptions);
  const division = sourcePo.division ?? props.division ?? "";
  const newPoDefaults = {
    deliverTo:
      sourcePo.deliverTo === ""
        ? (sourceWarehouse?.value ?? "")
        : sourcePo.deliverTo,
    shipDateValue: sourcePo.shipDateValue
      ? new Date(sourcePo.shipDateValue)
      : undefined,
    arrivalDate: sourcePo.arrivalDate
      ? new Date(sourcePo.arrivalDate)
      : undefined,
  };
  const candidates = buildDispositionCandidates(
    editablePos.value,
    sourcePo,
    releasedLines
  );
  decisionPending.value = true;
  showPanelModal(QuantityDispositionModal, {
    width: Math.min(640, Math.max(360, window.innerWidth - 32)),
    height: Math.min(600, Math.max(320, window.innerHeight - 32)),
    dialogId: options.dialogId,
    componentProps: {
      context: options.context,
      sourcePoSequence: poDisplayNumber(sourcePo),
      releasedLines,
      candidates,
      newPoDivision: division,
      newPoCubeCapacityFt3: props.cubeCapacityFt3,
      newPoDefaults,
      newPoWarehouseOptions: dispositionWarehouseOptions(division),
      onConfirm: (result: BatchDispositionResult) => {
        let newPo: EditableShippingPo | undefined;
        if (result.newPoMode && result.newPoMetadata) {
          newPo = blankPo(division);
          newPo.deliverTo = result.newPoMetadata.deliverTo;
          newPo.shipDateValue = result.newPoMetadata.shipDateValue
            ? new Date(result.newPoMetadata.shipDateValue)
            : undefined;
          newPo.arrivalDate = result.newPoMetadata.arrivalDate
            ? new Date(result.newPoMetadata.arrivalDate)
            : undefined;
          if (newPo.shipDateValue) {
            const year = String(newPo.shipDateValue.getFullYear()).padStart(
              4,
              "0"
            );
            const month = String(newPo.shipDateValue.getMonth() + 1).padStart(
              2,
              "0"
            );
            const day = String(newPo.shipDateValue.getDate()).padStart(2, "0");
            newPo.shipDate = `${year}-${month}-${day}`;
          }
        }
        const plan = planBatchDisposition(
          candidates,
          result.selections,
          releasedLines,
          !result.remainderToLeftover,
          newPo && result.newPoMode
            ? { newPo, newPoMode: result.newPoMode }
            : {}
        );
        applyPlanWithFeedback(plan, options.sourceLabel, newPo);
        options.onApplied();
      },
      onCancel: options.onCancelled,
    },
  });
}

function applyQuantityChange(
  line: EditableShippingPoLine,
  parsed: number,
  onApplied?: () => void,
  onRejected?: () => void
): void {
  if (parsed === line.qty) {
    line.qtyError = "";
    qtyDrafts.value.delete(line);
    onApplied?.();
    return;
  }
  if (parsed > line.qty) {
    const outcome = planQuantityIncrease(
      editablePos.value,
      line,
      parsed,
      sourceQtyBySku.value
    );
    if (outcome.kind === "insufficient") {
      line.qtyError = outcome.message;
      onRejected?.();
      return;
    }
    if (outcome.kind === "apply-directly") {
      const target = findPoForLine(line);
      if (!target) {
        throw new Error(`Unable to find suggested PO for SKU ${line.sku}.`);
      }
      pendingTransfers.push({
        sku: line.sku,
        quantity: parsed - line.qty,
        source: "Leftover inventory",
        destination: `PO ${String(target.sequence)}`,
      });
      line.qty = parsed;
      line.qtyError = "";
      qtyDrafts.value.delete(line);
      onApplied?.();
      return;
    }
    requestQuantityIncreaseConfirmation(
      outcome.plan,
      () => {
        line.qtyError = "";
        qtyDrafts.value.delete(line);
        onApplied?.();
      },
      () => {
        qtyDrafts.value.delete(line);
        onRejected?.();
      }
    );
    return;
  }

  const sourcePo = findPoForLine(line);
  if (!sourcePo) return;
  const released = line.qty - parsed;
  requestBatchDisposition({
    sourcePo,
    releasedLines: [toReleasedLine(sourcePo, line, released)],
    context: "reduce-line",
    dialogId: scopedId(`qty-disposition-${line.sku}-${sourcePo.id}`),
    sourceLabel: `PO ${String(poDisplayNumber(sourcePo))}`,
    onApplied: () => {
      line.qty = parsed;
      line.qtyError = "";
      qtyDrafts.value.delete(line);
      onApplied?.();
    },
    onCancelled: () => {
      qtyDrafts.value.delete(line);
      onRejected?.();
    },
  });
}

function removeSkuLineFromCapacityWarning(
  po: EditableShippingPo,
  line: EditableShippingPoLine
): void {
  priceDrafts.value.delete(line);
  qtyDrafts.value.delete(line);
  const lineIndex = po.lines.findIndex(
    (candidate) => candidate.sku === line.sku
  );
  if (lineIndex !== -1) po.lines.splice(lineIndex, 1);
}

function previewCapacityRedistribution(
  targetPo: EditableShippingPo,
  targetLine: EditableShippingPoLine,
  targetQty: number,
  sourceRow: BulkOrderCreationRow
) {
  const projectedLineByOriginal = new Map<
    EditableShippingPoLine,
    EditableShippingPoLine
  >();
  const projectedPoByOriginal = new Map<
    EditableShippingPo,
    EditableShippingPo
  >();
  const projectedPos = editablePos.value.map((po) => {
    const projectedPo = {
      ...po,
      lines: po.lines.map((line) => {
        const projectedLine = { ...line };
        projectedLineByOriginal.set(line, projectedLine);
        return projectedLine;
      }),
    };
    projectedPoByOriginal.set(po, projectedPo);
    return projectedPo;
  });
  const projectedTargetPo = projectedPoByOriginal.get(targetPo);
  const projectedTargetLine = projectedLineByOriginal.get(targetLine);
  if (!projectedTargetPo || !projectedTargetLine) {
    throw new Error(`Unable to preview capacity for SKU ${targetLine.sku}.`);
  }

  const increaseOutcome = planQuantityIncrease(
    projectedPos,
    projectedTargetLine,
    targetQty,
    sourceQtyBySku.value
  );
  if (increaseOutcome.kind === "needs-confirmation") {
    applyQuantityIncreasePlan(increaseOutcome.plan);
  } else if (increaseOutcome.kind === "apply-directly") {
    projectedTargetLine.qty = targetQty;
  }

  return planCapacityAwareRedistribution(
    projectedPos,
    projectedTargetPo,
    projectedTargetLine,
    targetQty,
    sourceQtyBySku.value,
    sourceRow
  );
}

function requestCapacityWarning(
  po: EditableShippingPo,
  line: EditableShippingPoLine,
  proposedQty: number,
  maxFittingQty: number,
  onSettled?: (accepted: boolean) => void
): void {
  const sourceRow = props.rows.find((row) => row.sku === line.sku);
  if (!sourceRow) {
    throw new Error(`Unable to find source row for SKU ${line.sku}.`);
  }
  const redistributionPlan = previewCapacityRedistribution(
    po,
    line,
    maxFittingQty,
    sourceRow
  );
  const onCancel = () => {
    qtyDrafts.value.delete(line);
    onSettled?.(false);
  };

  decisionPending.value = true;
  showPanelModal(CapacityWarningModal, {
    width: 560,
    height: 600,
    dialogId: scopedId(`capacity-warning-${line.sku}-${po.id}`),
    onClose: onCancel,
    componentProps: {
      sku: line.sku,
      poSequence: poDisplayNumber(po),
      proposedQty,
      maxFittingQty,
      unallocatedQty: redistributionPlan.unallocatedQty,
      remainingAfterRedistribution: redistributionPlan.remainingQty,
      redistributionPreview: redistributionPlan.candidates.map((candidate) => ({
        poSequence: poDisplayNumber(candidate.po),
        currentQty: candidate.line?.qty ?? 0,
        hasSkuLine: candidate.line !== undefined,
        maxAdditionalQty: candidate.maxAdditionalQty,
        additionalQty: candidate.additionalQty,
      })),
      onConfirm: (choice: CapacityWarningChoice) => {
        if (choice.type === "remove") {
          removeSkuLineFromCapacityWarning(po, line);
          onSettled?.(true);
          return;
        }

        const targetQty =
          choice.type === "override"
            ? proposedQty
            : choice.type === "manual"
              ? choice.quantity
              : maxFittingQty;
        const redistribute = choice.type === "redistribute";
        applyQuantityChange(
          line,
          targetQty,
          () => {
            if (redistribute) {
              const currentPlan = planCapacityAwareRedistribution(
                editablePos.value,
                po,
                line,
                line.qty,
                sourceQtyBySku.value,
                sourceRow
              );
              applyCapacityAwareRedistribution(currentPlan, line.sku);
              for (const candidate of currentPlan.candidates) {
                showMoveSuccess(candidate.po, [
                  {
                    sku: line.sku,
                    quantity: candidate.additionalQty,
                    source: "leftover inventory",
                  },
                ]);
              }
            }
            onSettled?.(true);
          },
          () => onSettled?.(false)
        );
      },
      onCancel,
    },
  });
}

function commitQty(
  line: EditableShippingPoLine,
  onSettled?: (accepted: boolean) => void
): void {
  if (!onSettled && (saving.value || decisionPending.value)) return;
  if (!canMutateLine(line)) {
    onSettled?.(false);
    return;
  }
  const draft = qtyDrafts.value.get(line);
  if (!draft) {
    onSettled?.(!line.qtyError);
    return;
  }
  if (!draft.needsCommit && !onSettled) return;
  draft.needsCommit = false;

  const parsed = parseWholeQuantityInput(draft.value);
  if (parsed === undefined || parsed < 0) {
    line.qtyError = "Enter a whole quantity of 0 or more.";
    onSettled?.(false);
    return;
  }
  if (parsed === line.qty) {
    line.qtyError = "";
    qtyDrafts.value.delete(line);
    onSettled?.(true);
    return;
  }

  if (parsed > line.qty) {
    const po = findPoForLine(line);
    const maxCubeQty = po ? getMaxQuantityWithinCapacity(po, line) : undefined;
    if (po && maxCubeQty !== undefined && parsed > maxCubeQty) {
      const maxFittingQty = Math.min(
        maxCubeQty,
        sourceQtyBySku.value.get(line.sku) ?? 0
      );
      requestCapacityWarning(po, line, parsed, maxFittingQty, onSettled);
      return;
    }
  }

  applyQuantityChange(
    line,
    parsed,
    () => onSettled?.(true),
    () => onSettled?.(false)
  );
}

function onPriceChange(line: EditableShippingPoLine, rawValue: string): void {
  if (!canMutateLine(line) || saving.value || decisionPending.value) return;
  priceDrafts.value.set(line, { value: rawValue, needsCommit: true });
}

function commitPrice(line: EditableShippingPoLine): void {
  if (!canMutateLine(line)) return;
  const draft = priceDrafts.value.get(line);
  if (!draft?.needsCommit) return;

  draft.needsCommit = false;
  line.priceError = applyPriceInput(line, draft.value);
  if (!line.priceError) priceDrafts.value.delete(line);
}

function commitOnBlur(
  line: EditableShippingPoLine,
  field: "qty" | "price",
  event: FocusEvent
): void {
  if (saving.value || decisionPending.value) return;
  if (
    event.relatedTarget instanceof Element &&
    event.relatedTarget.closest("[data-session-action]")
  )
    return;
  if (field === "qty") commitQty(line);
  else commitPrice(line);
}

/**
 * Removes `line` from `po`, first verifying where any currently-allocated
 * units should go (UX-37 rule 4). Lines with zero quantity have nothing to
 * redistribute, so they are removed immediately without a prompt.
 */
function removeLine(
  po: EditableShippingPo,
  line: EditableShippingPoLine
): void {
  if (
    (activeEditId.value && activeEditId.value !== po.id) ||
    saving.value ||
    decisionPending.value
  )
    return;
  const before = snapshotPos(editablePos.value);
  if (!activeEditId.value) pendingTransfers = [];
  const recordRemoval = () => {
    if (!activeEditId.value) {
      recordChanges(before, pendingTransfers);
      pendingTransfers = [];
    }
  };
  if (line.qty <= 0) {
    priceDrafts.value.delete(line);
    qtyDrafts.value.delete(line);
    po.lines = po.lines.filter((candidate) => candidate !== line);
    recordRemoval();
    return;
  }

  requestBatchDisposition({
    sourcePo: po,
    releasedLines: [toReleasedLine(po, line, line.qty)],
    context: "remove-line",
    dialogId: scopedId(`qty-disposition-${line.sku}-${po.id}`),
    sourceLabel: `PO ${String(poDisplayNumber(po))}`,
    onApplied: () => {
      priceDrafts.value.delete(line);
      qtyDrafts.value.delete(line);
      po.lines = po.lines.filter((candidate) => candidate !== line);
      recordRemoval();
    },
    onCancelled: () => {
      // Cancelled: leave the line and its quantity untouched.
    },
  });
}

function deliverToOptionsFor(po: EditableShippingPo): DropdownOption[] {
  return getDeliverToOptions(
    currentDeliverToByPo.value.get(po.id),
    props.warehouseOptions
  );
}

function deliverToLabel(po: EditableShippingPo): string {
  const option = props.warehouseOptions.find(
    (candidate) => candidate.value === po.deliverTo
  );
  return option?.label ?? "Not set";
}

function formatDateForDisplay(value: Date | undefined): string {
  return value ? formatNeededByForDisplay(value) : "Not set";
}

function addSkuOptionsFor(po: EditableShippingPo): DropdownOption[] {
  const onPo = new Set(po.lines.map((line) => line.sku));
  const seen = new Set<string>();
  const options: DropdownOption[] = [];
  for (const row of props.rows) {
    if (onPo.has(row.sku) || seen.has(row.sku)) continue;
    seen.add(row.sku);
    options.push({
      label: `${row.sku} \u2014 ${row.description}`,
      value: row.sku,
    });
  }
  return options;
}

function getAddSkuDrafts(po: EditableShippingPo): (string | undefined)[] {
  addSkuDrafts.value[po.id] ??= [undefined];
  return addSkuDrafts.value[po.id] ?? [undefined];
}

function addSkuPickerRow(po: EditableShippingPo): void {
  if (activeEditId.value !== po.id || saving.value || decisionPending.value)
    return;
  getAddSkuDrafts(po).push(undefined);
}

function onAddSkuSelected(
  po: EditableShippingPo,
  index: number,
  sku: string | undefined
): void {
  if (activeEditId.value !== po.id || saving.value || decisionPending.value)
    return;
  const drafts = getAddSkuDrafts(po);
  if (!sku) {
    drafts[index] = undefined;
    return;
  }
  const row = props.rows.find((candidate) => candidate.sku === sku);
  if (row) addSkuLineToPo(po, row);
  if (drafts.length > 1) drafts.splice(index, 1);
  else drafts[index] = undefined;
}

// UX-33: section-level submission options. UI-only until backend support exists.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ccEmails = ref<string[]>([
  "purchasing.team@example.com",
  "dc.manager@example.com",
]);
const ccEmailDraft = ref<string | undefined>("");
const ccEmailError = ref("");
const sendToFaxQueue = ref(false);
const requestEmailConfirmation = ref(false);
const confirmationEmail = ref<string | undefined>("");
const confirmationEmailError = ref("");

function addCcEmail(): void {
  const email = (ccEmailDraft.value ?? "").trim();
  if (email === "") return;
  if (!EMAIL_PATTERN.test(email)) {
    ccEmailError.value = "Enter a valid email address.";
    return;
  }
  if (ccEmails.value.some((cc) => cc.toLowerCase() === email.toLowerCase())) {
    ccEmailError.value = "That address is already on the CC list.";
    return;
  }
  ccEmails.value.push(email);
  ccEmailDraft.value = "";
  ccEmailError.value = "";
}

function removeCcEmail(email: string): void {
  ccEmails.value = ccEmails.value.filter((cc) => cc !== email);
}

function validateConfirmationEmail(): boolean {
  if (!requestEmailConfirmation.value) {
    confirmationEmailError.value = "";
    return true;
  }
  const email = (confirmationEmail.value ?? "").trim();
  if (email === "") {
    confirmationEmailError.value = "Confirmation email address is required.";
    return false;
  }
  if (!EMAIL_PATTERN.test(email)) {
    confirmationEmailError.value = "Enter a valid email address.";
    return false;
  }
  confirmationEmailError.value = "";
  return true;
}

watch(requestEmailConfirmation, (checked) => {
  if (!checked) confirmationEmailError.value = "";
});

/**
 * Removes `poId` from the panel and clears its per-PO UI state. Any released
 * units must already have been dispositioned (UX-17); this only finalizes the
 * removal and commits the accumulated transfer records.
 */
function finalizePoDeletion(
  poId: string,
  changesBefore: PoChangeSnapshot[]
): void {
  const po = editablePos.value.find((candidate) => candidate.id === poId);
  if (po) {
    for (const line of po.lines) {
      priceDrafts.value.delete(line);
      qtyDrafts.value.delete(line);
    }
  }
  editablePos.value = editablePos.value.filter(
    (candidate) => candidate.id !== poId
  );
  Reflect.deleteProperty(leftoverMoveNotices.value, poId);
  Reflect.deleteProperty(leftoverMoveHighlightedSkusByPo.value, poId);
  Reflect.deleteProperty(selectedLinesByPo.value, poId);
  recordChanges(changesBefore, pendingTransfers);
  pendingTransfers = [];
}

/** Confirms deleting a PO whose lines are all zero quantity (no disposition). */
function deletePoConfirmed(poId: string): void {
  if (activeEditId.value) return;
  const changesBefore = snapshotPos(editablePos.value);
  pendingTransfers = [];
  finalizePoDeletion(poId, changesBefore);
}

function requestDeletePo(po: EditableShippingPo): void {
  if (
    activeEditId.value ||
    decisionPending.value ||
    leftoverMoveModalOpen.value
  )
    return;
  const positiveLines = po.lines.filter((line) => line.qty > 0);
  if (positiveLines.length === 0) {
    showPanelModal(OKCancelModal, {
      width: 420,
      height: 200,
      dialogId: scopedId(`delete-${po.id}`),
      componentProps: {
        title: "Delete Suggested PO",
        message: `Delete PO ${String(poDisplayNumber(po))}? This removes all ${String(po.lines.length)} SKU line(s) from this suggested PO.`,
        okButtonText: "Delete PO",
        cancelButtonText: "Cancel",
        onOk: () => {
          deletePoConfirmed(po.id);
        },
      },
    });
    return;
  }
  // UX-17: deleting a PO with allocated units opens the aggregate disposition
  // dialog (one confirm for all SKUs) instead of a generic confirmation.
  const changesBefore = snapshotPos(editablePos.value);
  pendingTransfers = [];
  requestBatchDisposition({
    sourcePo: po,
    releasedLines: positiveLines.map((line) =>
      toReleasedLine(po, line, line.qty)
    ),
    context: "delete-po",
    dialogId: scopedId(`delete-${po.id}`),
    sourceLabel: `PO ${String(poDisplayNumber(po))} (deleted)`,
    onApplied: () => {
      finalizePoDeletion(po.id, changesBefore);
    },
    onCancelled: () => {
      pendingTransfers = [];
    },
  });
}

async function editPo(poId: string): Promise<void> {
  if (
    activeEditId.value ||
    modalStore.stack.some((entry) => ownedDialogs.has(entry.dialogId))
  )
    return;
  invalidatePendingMutations();
  editSnapshot = snapshotPos(editablePos.value);
  pendingTransfers = [];
  // Preserve object identities: draft maps use line references. All competing
  // PO mutations are locked, so this journal contains session-owned changes only.
  const snapshots = editablePos.value.map((po) => ({
    po,
    state: {
      ...po,
      shipDateValue: cloneMoveDate(po.shipDateValue),
      arrivalDate: cloneMoveDate(po.arrivalDate),
    },
    lines: po.lines.map((line) => ({
      line,
      state: { ...line, neededBy: cloneMoveDate(line.neededBy) },
    })),
  }));
  const prices = new Map(
    [...priceDrafts.value].map(([line, draft]) => [line, { ...draft }])
  );
  const quantities = new Map(
    [...qtyDrafts.value].map(([line, draft]) => [line, { ...draft }])
  );
  const additions = Object.fromEntries(
    Object.entries(addSkuDrafts.value).map(([id, drafts]) => [id, [...drafts]])
  );
  const notices = { ...leftoverMoveNotices.value };
  const highlights = Object.fromEntries(
    Object.entries(leftoverMoveHighlightedSkusByPo.value).map(([id, skus]) => [
      id,
      [...skus],
    ])
  );
  restoreSession = () => {
    editablePos.value = snapshots.map(({ po }) => po);
    for (const { po, state, lines } of snapshots) {
      Object.assign(po, state);
      po.lines = lines.map(({ line, state: lineState }) => {
        Object.assign(line, lineState);
        return line;
      });
    }
    priceDrafts.value = prices;
    qtyDrafts.value = quantities;
    addSkuDrafts.value = additions;
    leftoverMoveNotices.value = notices;
    leftoverMoveHighlightedSkusByPo.value = highlights;
  };
  activeEditId.value = poId;
  editingPoIds.value[poId] = true;
  await nextTick();
  cardRefs[poId]
    ?.querySelector<HTMLInputElement>(".shipping-po-panel-qty-input")
    ?.focus();
}

async function finishEdit(poId: string): Promise<void> {
  invalidatePendingMutations();
  editingPoIds.value = {};
  activeEditId.value = undefined;
  selectedLinesByPo.value[poId] = [];
  restoreSession = undefined;
  editSnapshot = [];
  pendingTransfers = [];
  if (deliverToDefaultsPending) resolveDeliverToDefaults();
  await nextTick();
  cardRefs[poId]
    ?.querySelector<HTMLButtonElement>("[data-testid='edit-po-button']")
    ?.focus();
}

async function cancelEdit(poId: string): Promise<void> {
  if (activeEditId.value !== poId) return;
  invalidatePendingMutations();
  restoreSession?.();
  await finishEdit(poId);
}

async function saveEdit(po: EditableShippingPo): Promise<void> {
  if (activeEditId.value !== po.id || saving.value || decisionPending.value)
    return;
  const epoch = mutationEpoch;
  saving.value = true;
  let accepted = true;
  try {
    // Validate all scalar drafts before opening any quantity confirmation.
    for (const line of po.lines) {
      commitPrice(line);
      const draft = qtyDrafts.value.get(line);
      const qty = draft ? parseWholeQuantityInput(draft.value) : line.qty;
      line.qtyError =
        qty === undefined || qty < 0
          ? "Enter a whole quantity of 0 or more."
          : "";
      if (line.qtyError || line.priceError) accepted = false;
    }
    if (!accepted) return;
    const linesToCommit = [...po.lines];
    for (const line of linesToCommit) {
      if (!po.lines.includes(line)) continue;
      accepted = await new Promise<boolean>((resolve) => {
        const settle = (value: boolean) => {
          pendingSaves.delete(settle);
          resolve(value);
        };
        pendingSaves.add(settle);
        commitQty(line, settle);
      });
      if (!accepted || epoch !== mutationEpoch) return;
    }
    if (epoch === mutationEpoch) {
      recordChanges(editSnapshot, pendingTransfers);
      await finishEdit(po.id);
    }
  } finally {
    if (epoch === mutationEpoch) {
      saving.value = false;
      if (!accepted) {
        await nextTick();
        const error = cardRefs[po.id]?.querySelector(
          ".shipping-po-panel-field-error"
        );
        const input =
          error?.parentElement?.querySelector<HTMLInputElement>("input") ??
          cardRefs[po.id]?.querySelector<HTMLInputElement>(
            ".shipping-po-panel-qty-input"
          );
        input?.focus();
      }
    }
  }
}

function toggleMoreMenu(): void {
  moreMenuOpen.value = !moreMenuOpen.value;
}

function closeMoreMenu(): void {
  moreMenuOpen.value = false;
}

function handleCreatePos(): void {
  const confirmationValid = validateConfirmationEmail();
  if (!shipVia.value) {
    shipViaError.value = "Select a Ship Via option before creating POs.";
    return;
  }
  shipViaError.value = "";
  if (!confirmationValid) return;
  actionNotice.value = `Create POs ${NOT_IMPLEMENTED_NOTICE}`;
}

function handleMoreAction(
  action: "Queue POs" | "Preview in NWF" | "On Order"
): void {
  closeMoreMenu();
  actionNotice.value = `${action} ${NOT_IMPLEMENTED_NOTICE}`;
}

function toggleLeftoverInventory(): void {
  leftoverInventoryOpen.value = !leftoverInventoryOpen.value;
}

function cloneMoveDate(date: Date | undefined): Date | undefined {
  return date ? new Date(date) : undefined;
}

function buildLeftoverMoveDraft(skus: string[]): LeftoverInventoryMoveDraft {
  const existing = leftoverMoveDraft.value;
  const selectedSkus = [...new Set(skus)].filter((sku) =>
    liveLeftoverRows.value.some((row) => row.sku === sku)
  );
  const quantitiesBySku = { ...existing?.quantitiesBySku };
  const sourceLeftoversBySku = { ...existing?.sourceLeftoversBySku };
  for (const sku of selectedSkus) {
    const row = liveLeftoverRows.value.find(
      (candidate) => candidate.sku === sku
    );
    if (!row) continue;
    quantitiesBySku[sku] ??= String(row.requestedUnits);
    sourceLeftoversBySku[sku] ??= row.requestedUnits;
  }

  const targetDatesByPo = Object.fromEntries(
    editablePos.value.map((po) => {
      const priorDates = existing?.targetDatesByPo[po.id];
      return [
        po.id,
        {
          ...(priorDates?.shipDateValue
            ? { shipDateValue: cloneMoveDate(priorDates.shipDateValue) }
            : po.shipDateValue
              ? { shipDateValue: cloneMoveDate(po.shipDateValue) }
              : {}),
          ...(priorDates?.arrivalDate
            ? { arrivalDate: cloneMoveDate(priorDates.arrivalDate) }
            : po.arrivalDate
              ? { arrivalDate: cloneMoveDate(po.arrivalDate) }
              : {}),
        },
      ];
    })
  );
  const targetPoId = editablePos.value.some(
    (po) => po.id === existing?.targetPoId
  )
    ? (existing?.targetPoId ?? "")
    : (editablePos.value[0]?.id ?? "");
  let fitInitialized = false;
  if (existing?.targetPoId === targetPoId) {
    const sameSelection =
      selectedSkus.length === existing.selectedSkus.length &&
      selectedSkus.every((sku, index) => sku === existing.selectedSkus[index]);
    if (sameSelection) fitInitialized = existing.fitInitialized ?? false;
  }

  return {
    selectedSkus,
    sourceLeftoversBySku,
    targetPoId,
    quantitiesBySku,
    mode: existing?.mode ?? "fit",
    stage: existing?.stage ?? "edit",
    fitInitialized,
    targetDatesByPo,
  };
}

function getLeftoverMoveSnapshot(): LeftoverInventoryMoveSnapshot {
  return {
    rows: liveLeftoverRows.value,
    targets: [
      ...editablePos.value,
      ...(newLeftoverTarget.value ? [newLeftoverTarget.value] : []),
    ],
  };
}

function onLeftoverMoveDraftChange(draft: LeftoverInventoryMoveDraft): void {
  leftoverMoveDraft.value = draft;
}

function dismissLeftoverMoveModal(): void {
  void nextTick(() => {
    document
      .querySelector<HTMLElement>(
        `#${CSS.escape(scopedId("leftover-inventory-move-button"))}`
      )
      ?.focus();
  });
}

function applyLeftoverInventoryMove(
  request: LeftoverInventoryMoveApplyRequest
): LeftoverInventoryMoveApplyResult {
  if (activeEditId.value)
    return {
      targetError:
        "Save or Cancel the active PO edit before moving leftover inventory.",
      applied: [],
      skipped: [],
    };
  const target =
    editablePos.value.find((po) => po.id === request.targetPoId) ??
    (newLeftoverTarget.value?.id === request.targetPoId
      ? newLeftoverTarget.value
      : undefined);
  if (!target) {
    return {
      targetError: "The selected target PO is no longer available.",
      applied: [],
      skipped: [],
    };
  }

  const currentRows = liveLeftoverRows.value;
  const sourceRowBySku = new Map(currentRows.map((row) => [row.sku, row]));
  const requestedQuantities: Record<string, number> = {};
  const skipped: { sku: string; reason: string }[] = [];

  for (const sku of request.selectedSkus) {
    const row = sourceRowBySku.get(sku);
    if (!row) {
      skipped.push({ sku, reason: "No longer has leftover inventory." });
      continue;
    }
    const rawQuantity = request.quantitiesBySku[sku] ?? "";
    const quantity = parseWholeQuantityInput(rawQuantity);
    if (quantity === undefined || quantity < 0) {
      skipped.push({
        sku,
        reason: "Move quantity must be a whole number of 0 or more.",
      });
      continue;
    }
    if (quantity > row.requestedUnits) {
      skipped.push({
        sku,
        reason: `Only ${String(row.requestedUnits)} units remain.`,
      });
      continue;
    }
    if (quantity === 0) {
      skipped.push({ sku, reason: "Move quantity is zero." });
      continue;
    }
    requestedQuantities[sku] = quantity;
  }

  if (request.mode === "fit" && Object.keys(requestedQuantities).length > 0) {
    const plan = planProportionalLeftoverMove(
      target,
      currentRows,
      requestedQuantities
    );
    if (!plan.capacityMeasurable) {
      return {
        targetError:
          "Capacity cannot be measured. Choose the explicit override to continue.",
        applied: [],
        skipped,
      };
    }
    if (
      Object.entries(requestedQuantities).some(
        ([sku, quantity]) => plan.quantitiesBySku[sku] !== quantity
      )
    ) {
      return {
        requiresReview: true,
        quantitiesBySku: plan.quantitiesBySku,
        applied: [],
        skipped,
      };
    }
  }

  const projectedPo = buildProjectedPoAfterMove(
    target,
    currentRows,
    requestedQuantities
  );
  const projectedCapacity = getAvailableCapacityFt3(projectedPo);
  const capacityNeedsOverride =
    projectedCapacity === undefined || projectedCapacity < 0;
  if (
    Object.keys(requestedQuantities).length > 0 &&
    request.mode === "override" &&
    capacityNeedsOverride &&
    !request.overrideConfirmed
  ) {
    return {
      targetError: "Confirm the capacity override before applying this move.",
      applied: [],
      skipped,
    };
  }

  const applied: { sku: string; quantity: number }[] = [];
  const before = snapshotPos(editablePos.value);
  pendingTransfers = [];
  for (const [sku, quantity] of Object.entries(requestedQuantities)) {
    const row = sourceRowBySku.get(sku);
    if (!row) {
      skipped.push({ sku, reason: "No longer has leftover inventory." });
      continue;
    }

    let line = target.lines.find((candidate) => candidate.sku === sku);
    if (!line) {
      addSkuLineToPo(target, row);
      line = target.lines.find((candidate) => candidate.sku === sku);
    }
    if (!line) {
      throw new Error(`Unable to add SKU ${sku} to suggested PO ${target.id}.`);
    }
    line.qty += quantity;
    line.qtyError = "";
    applied.push({ sku, quantity });
  }

  if (applied.length > 0) {
    if (target === newLeftoverTarget.value) {
      editablePos.value.push(target);
      newLeftoverTarget.value = undefined;
    }
    target.shipDateValue = cloneMoveDate(request.shipDateValue);
    target.arrivalDate = cloneMoveDate(request.arrivalDate);
    showMoveSuccess(
      target,
      applied.map((entry) => ({
        ...entry,
        source: "leftover inventory",
      }))
    );
    leftoverMoveDraft.value = undefined;
    recordChanges(before, pendingTransfers);
    pendingTransfers = [];
  }
  return { applied, skipped };
}

function openLeftoverMoveModal(skus: string[]): void {
  if (activeEditId.value || decisionPending.value) return;
  const firstSelectedRow = liveLeftoverRows.value.find((row) =>
    skus.includes(row.inventoryKey ?? row.sku)
  );
  newLeftoverTarget.value = blankPo(firstSelectedRow?.division);
  const epoch = mutationEpoch;
  let dismissed = false;
  const dismiss = () => {
    if (epoch !== mutationEpoch || dismissed) return;
    dismissed = true;
    dismissLeftoverMoveModal();
  };
  const draft = buildLeftoverMoveDraft(skus);
  if (draft.selectedSkus.length === 0) return;
  leftoverSelectedSkus.value = [...draft.selectedSkus];
  leftoverMoveDraft.value = draft;
  const width = Math.min(1000, Math.max(320, window.innerWidth - 32));
  const height = Math.min(760, Math.max(240, window.innerHeight - 32));
  showPanelModal(LeftoverInventoryMoveModal, {
    width,
    height,
    enableResizing: false,
    closeOnOverlayClick: false,
    closeOnEscapeKeyPressed: true,
    dialogId: leftoverMoveDialogId.value,
    onClose: dismiss,
    componentProps: {
      idPrefix: props.idPrefix,
      rows: liveLeftoverRows.value,
      targets: getLeftoverMoveSnapshot().targets,
      newPoId: newLeftoverTarget.value.id,
      initialDraft: draft,
      getCurrentState: getLeftoverMoveSnapshot,
      onDraftChange: (draft: LeftoverInventoryMoveDraft) => {
        if (epoch === mutationEpoch && !dismissed)
          onLeftoverMoveDraftChange(draft);
      },
      onApply: (request: LeftoverInventoryMoveApplyRequest) =>
        epoch === mutationEpoch && !dismissed
          ? applyLeftoverInventoryMove(request)
          : {
              targetError: "This move is no longer active.",
              applied: [],
              skipped: [],
            },
      onDismiss: dismiss,
    },
  });
}

function onDocumentClick(event: MouseEvent): void {
  if (!moreMenuOpen.value) return;
  if (moreMenuRef.value && !moreMenuRef.value.contains(event.target as Node)) {
    closeMoreMenu();
  }
}

function onDocumentKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape" && moreMenuOpen.value) closeMoreMenu();
}

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
  document.addEventListener("keydown", onDocumentKeydown);
});

onUnmounted(() => {
  invalidatePendingMutations();
  document.removeEventListener("click", onDocumentClick);
  document.removeEventListener("keydown", onDocumentKeydown);
});
</script>

<template>
  <div
    :class="[
      'shipping-po-panel',
      activeLayout === 'rail' ? 'shipping-po-panel-rail-layout' : '',
      activeLayout === 'grid' ? 'shipping-po-panel-grid-layout' : '',
    ]"
  >
    <StatusBanner
      v-if="
        editablePos.some((po) =>
          po.lines.some((line) => line.unitPriceUsd === null)
        )
      "
      variant="warning"
      role="alert"
    >
      FirstCost is unavailable for some SKUs. Enter their each-unit prices
      manually. Totals remain unavailable while allocated units lack pricing.
    </StatusBanner>

    <div class="shipping-po-panel-section-header">
      <div class="shipping-po-panel-section-header-total">
        <span class="shipping-po-panel-context-label">Total cost</span>
        <strong>{{ formatCurrencyUsd(headerTotalCostUsd) }}</strong>
      </div>

      <div class="shipping-po-panel-section-header-selects">
        <FormField label="Flag PO As" label-placement="top">
          <Dropdown
            :id="scopedId('flag-po-as-select')"
            v-model="flagPoAs"
            :options="FLAG_PO_AS_OPTIONS"
            placeholder="Select"
          />
        </FormField>
        <FormField label="Terms" label-placement="top">
          <Dropdown
            :id="scopedId('terms-select')"
            v-model="terms"
            :options="TERMS_OPTIONS"
            placeholder="Select"
          />
        </FormField>
        <FormField label="Ship Via" label-placement="top" required>
          <Dropdown
            :id="scopedId('ship-via-select')"
            v-model="shipVia"
            :options="SHIP_VIA_OPTIONS"
            placeholder="Select"
          />
          <p
            v-if="shipViaError"
            class="shipping-po-panel-field-error"
            role="alert"
          >
            {{ shipViaError }}
          </p>
        </FormField>
      </div>

      <div class="shipping-po-panel-section-header-actions">
        <button
          type="button"
          class="btn btn-secondary"
          data-testid="add-blank-po-button"
          :disabled="
            Boolean(activeEditId) || decisionPending || leftoverMoveModalOpen
          "
          @click="addBlankPo"
        >
          Add blank PO
        </button>
        <button
          v-if="liveLeftoverRows.length > 0 || leftoverInventoryOpen"
          type="button"
          class="btn btn-secondary"
          data-testid="leftover-inventory-button"
          :aria-controls="scopedId('leftover-inventory-sidebar')"
          :aria-expanded="leftoverInventoryOpen"
          :aria-label="leftoverInventoryButtonLabel"
          @click="toggleLeftoverInventory"
        >
          Leftover POs ({{ liveLeftoverRows.length }})
        </button>
        <button
          type="button"
          class="btn btn-primary"
          data-testid="create-pos-button"
          @click="handleCreatePos"
        >
          Create POs
        </button>
        <div ref="moreMenuRef" class="shipping-po-panel-more-menu">
          <button
            v-if="showLayoutToggle"
            type="button"
            class="shipping-po-panel-icon-button shipping-po-panel-layout-toggle"
            data-testid="po-grid-toggle-button"
            :aria-label="
              gridLayoutEnabled
                ? 'Show suggested POs in list view'
                : 'Show suggested POs in grid view'
            "
            :title="
              gridLayoutEnabled
                ? 'Show suggested POs in list view'
                : 'Show suggested POs in grid view'
            "
            :aria-pressed="gridLayoutEnabled"
            @click="gridLayoutEnabled = !gridLayoutEnabled"
          >
            <LucideIcon
              :name="gridLayoutEnabled ? 'rows-3' : 'grid-2x2'"
              :size="16"
            />
          </button>
          <button
            type="button"
            class="btn btn-secondary"
            aria-haspopup="menu"
            :aria-expanded="moreMenuOpen"
            data-testid="more-actions-button"
            @click="toggleMoreMenu"
          >
            More
            <LucideIcon name="chevron-down" :size="16" />
          </button>
          <ul
            v-if="moreMenuOpen"
            role="menu"
            aria-label="More PO actions"
            class="shipping-po-panel-more-menu-list"
          >
            <li role="none">
              <button
                role="menuitem"
                type="button"
                @click="handleMoreAction('Queue POs')"
              >
                Queue POs
              </button>
            </li>
            <li role="none">
              <button
                role="menuitem"
                type="button"
                @click="handleMoreAction('Preview in NWF')"
              >
                Preview in NWF
              </button>
            </li>
            <li role="none">
              <button
                role="menuitem"
                type="button"
                @click="handleMoreAction('On Order')"
              >
                On Order
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <p
      v-if="actionNotice"
      class="shipping-po-panel-action-notice"
      role="status"
    >
      {{ actionNotice }}
    </p>
    <p v-if="activeEditId" role="status" class="text-sm">
      Save or Cancel the active PO edit before editing another PO, deleting a
      PO, adding a blank PO, or creating a new move target. Save accepts changes
      locally only.
    </p>

    <div :class="`shipping-po-panel-${activeLayout}`">
      <div
        v-for="po in editablePos"
        :key="po.id"
        :ref="(el) => setCardRef(po.id, el as Element | null)"
        :data-po-id="po.id"
        :class="[
          'shipping-po-panel-card',
          activeLayout === 'rail' ? 'shipping-po-panel-rail-card' : '',
        ]"
      >
        <div
          class="shipping-po-panel-card-header"
          :style="editingPoIds[po.id] ? { paddingRight: '8.5rem' } : undefined"
        >
          <strong>PO {{ poDisplayNumber(po) }} of {{ po.totalCount }}</strong>
          <span v-if="po.division" data-testid="po-division-label">
            Division {{ po.division }}
          </span>
          <span>Ship date {{ formatCalendarDateForDisplay(po.shipDate) }}</span>
          <div class="shipping-po-panel-card-controls">
            <button
              type="button"
              class="shipping-po-panel-icon-button"
              data-testid="edit-po-button"
              :aria-label="`${editingPoIds[po.id] ? 'Save' : 'Edit'} PO ${poDisplayNumber(po)}`"
              :disabled="
                Boolean(activeEditId && activeEditId !== po.id) ||
                saving ||
                decisionPending
              "
              data-session-action="save"
              @pointerdown.prevent
              @mousedown.prevent
              @click="editingPoIds[po.id] ? saveEdit(po) : editPo(po.id)"
            >
              <LucideIcon
                :name="editingPoIds[po.id] ? 'save' : 'pencil'"
                :size="16"
              />
            </button>
            <button
              v-if="editingPoIds[po.id]"
              type="button"
              class="shipping-po-panel-icon-button"
              :aria-label="`Cancel editing PO ${poDisplayNumber(po)}`"
              data-session-action="cancel"
              @pointerdown.prevent
              @mousedown.prevent
              @click="cancelEdit(po.id)"
            >
              Cancel
            </button>
            <button
              type="button"
              class="shipping-po-panel-icon-button"
              data-testid="delete-po-button"
              :aria-label="`Delete PO ${poDisplayNumber(po)}`"
              :disabled="
                Boolean(activeEditId) ||
                decisionPending ||
                leftoverMoveModalOpen
              "
              @click="requestDeletePo(po)"
            >
              <LucideIcon name="x" :size="16" />
            </button>
          </div>
        </div>
        <p
          v-if="leftoverMoveNotices[po.id]"
          class="shipping-po-panel-leftover-move-notice"
          data-testid="leftover-move-notice"
          role="status"
        >
          {{ leftoverMoveNotices[po.id] }}
        </p>
        <div class="shipping-po-panel-card-stats">
          <button
            v-if="editingPoIds[po.id]"
            type="button"
            class="btn btn-secondary"
            data-testid="move-selected-new-po-button"
            :disabled="
              leftoverMoveModalOpen ||
              decisionPending ||
              !selectedLinesByPo[po.id]?.length
            "
            @click="moveSelectedLines(po)"
          >
            Move selected lines to new PO
          </button>
          <span>{{ poTotals(po).totalCubicFeet }} ft³ cube</span>
          <span>{{ formatCurrencyUsd(poTotals(po).totalCostUsd) }} cost</span>
          <AppPopover
            v-if="committedChanges[po.id]?.length"
            class="shipping-po-panel-updated-indicator"
            tabindex="-1"
            tooltip
            :trigger="['hover', 'focus']"
          >
            <template #trigger="{ tooltipId }">
              <button
                type="button"
                class="shipping-po-panel-updates-button"
                :aria-label="`View updates to PO ${po.sequence}`"
                :aria-describedby="tooltipId"
                @click="showPoChanges(po)"
              >
                <LucideIcon name="circle-help" :size="16" />
                Updated
              </button>
            </template>
            Committed changes since generation. Activate to view details.
          </AppPopover>
        </div>
        <div class="shipping-po-panel-utilization">
          <div class="shipping-po-panel-utilization-track">
            <div
              class="shipping-po-panel-utilization-fill"
              :class="`shipping-po-panel-utilization-fill-${getUtilizationStatus(poTotals(po).loadPercent)}`"
              :style="{
                width: `${String(Math.min(100, poTotals(po).loadPercent))}%`,
              }"
            ></div>
          </div>
          <span
            >Load {{ poTotals(po).loadPercent }}% of capacity ({{
              capacityDescription(po)
            }}) · {{ po.lines.length }} SKU lines</span
          >
        </div>
        <div class="shipping-po-panel-table-wrap">
          <table class="shipping-po-panel-table">
            <thead>
              <tr>
                <th scope="col" class="shipping-po-panel-sku-column">SKU</th>
                <th scope="col" class="shipping-po-panel-qty-column">Qty</th>
                <th
                  v-if="showNeededByColumn"
                  scope="col"
                  class="shipping-po-panel-needed-by-column"
                >
                  Needed By
                </th>
                <th scope="col" class="shipping-po-panel-price-column">Each</th>
                <th scope="col" class="w-20">% capacity</th>
                <th scope="col" class="shipping-po-panel-remove-column">
                  <span class="sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <AppPopover
                v-for="line in po.lines"
                :key="line.sku"
                as="tr"
                tooltip
                :trigger="['hover', 'focus']"
                :width="280"
                :class="{
                  'shipping-po-panel-leftover-move-highlight': (
                    leftoverMoveHighlightedSkusByPo[po.id] ?? []
                  ).includes(line.sku),
                }"
              >
                <template #trigger="{ show, tooltipId }">
                  <td class="shipping-po-panel-sku-column">
                    <input
                      v-if="editingPoIds[po.id]"
                      type="checkbox"
                      :aria-label="`Select SKU ${line.sku} on PO ${poDisplayNumber(po)} for new PO`"
                      :checked="
                        selectedLinesByPo[po.id]?.includes(line.sku) ?? false
                      "
                      :disabled="saving || decisionPending"
                      @change="
                        selectLine(
                          po,
                          line.sku,
                          ($event.target as HTMLInputElement).checked
                        )
                      "
                    />
                    <button
                      type="button"
                      :aria-label="`Details for SKU ${line.sku}`"
                      :aria-describedby="tooltipId"
                      class="cursor-help underline decoration-dotted"
                      @click="show"
                    >
                      {{ line.sku }}
                    </button>
                  </td>
                  <td class="shipping-po-panel-qty-column">
                    <Textbox
                      v-if="editingPoIds[po.id]"
                      :model-value="
                        qtyDrafts.get(line)?.value ?? String(line.qty)
                      "
                      :label="`Qty for SKU ${line.sku}`"
                      placeholder="0"
                      class="shipping-po-panel-qty-input"
                      :disabled="saving || decisionPending"
                      @update:model-value="
                        (value) => onQtyChange(line, value ?? '')
                      "
                      @blur="commitOnBlur(line, 'qty', $event)"
                      @keydown.enter.prevent="commitQty(line)"
                    />
                    <span
                      v-else
                      class="shipping-po-panel-readonly-value shipping-po-panel-qty-value"
                      >{{ line.qty }}</span
                    >
                    <p
                      v-if="line.qtyError"
                      class="shipping-po-panel-field-error"
                      role="alert"
                    >
                      {{ line.qtyError }}
                    </p>
                  </td>
                  <td
                    v-if="showNeededByColumn"
                    class="shipping-po-panel-needed-by-cell shipping-po-panel-needed-by-column"
                  >
                    <FormField
                      v-if="editingPoIds[po.id]"
                      label="Needed By"
                      label-placement="top"
                    >
                      <DatePicker
                        v-model="line.neededBy"
                        placeholder="MM/DD/YYYY"
                      />
                    </FormField>
                    <span
                      v-else
                      class="shipping-po-panel-readonly-value shipping-po-panel-needed-by-value"
                      >{{ formatNeededByForDisplay(line.neededBy) }}</span
                    >
                  </td>
                  <td class="shipping-po-panel-price-column">
                    <Textbox
                      v-if="editingPoIds[po.id]"
                      :model-value="
                        priceDrafts.get(line)?.value ??
                        (line.unitPriceUsd === null
                          ? ''
                          : formatCurrencyUsd(line.unitPriceUsd))
                      "
                      :label="`Each price for SKU ${line.sku}`"
                      placeholder="Unavailable"
                      class="shipping-po-panel-price-input"
                      :disabled="saving || decisionPending"
                      @update:model-value="
                        (value) => onPriceChange(line, value ?? '')
                      "
                      @blur="commitOnBlur(line, 'price', $event)"
                      @keydown.enter.prevent="commitPrice(line)"
                    />
                    <span
                      v-else
                      class="shipping-po-panel-readonly-value shipping-po-panel-price-value"
                      >{{ formatCurrencyUsd(line.unitPriceUsd) }}</span
                    >
                    <p
                      v-if="line.priceError"
                      class="shipping-po-panel-field-error"
                      role="alert"
                    >
                      {{ line.priceError }}
                    </p>
                  </td>
                  <td data-testid="line-capacity-percent">
                    {{ formatLineCapacityPercent(po, line) }}
                  </td>
                  <td class="shipping-po-panel-remove-column">
                    <button
                      type="button"
                      class="shipping-po-panel-icon-button"
                      :aria-label="`Remove SKU ${line.sku} from PO ${poDisplayNumber(po)}`"
                      :disabled="
                        Boolean(activeEditId && activeEditId !== po.id) ||
                        saving ||
                        decisionPending
                      "
                      @click="removeLine(po, line)"
                    >
                      <LucideIcon name="trash" :size="16" />
                    </button>
                  </td>
                </template>
                <p>{{ line.description || "Description unavailable" }}</p>
                <p>
                  Cube per unit:
                  {{
                    Number.isFinite(line.cubicFeetPerUnit) &&
                    line.cubicFeetPerUnit >= 0
                      ? `${line.cubicFeetPerUnit.toFixed(2)} ft³`
                      : "Unavailable"
                  }}
                </p>
              </AppPopover>
            </tbody>
          </table>
        </div>
        <div class="shipping-po-panel-card-footer" data-testid="po-card-footer">
          <fieldset
            class="m-0 min-w-0 border-0 p-0"
            :disabled="saving || decisionPending"
          >
            <div class="shipping-po-panel-footer-row">
              <span
                :id="scopedId(`${po.id}-add-sku-label`)"
                class="shipping-po-panel-footer-label"
                >Add SKU to PO</span
              >
              <div
                v-if="editingPoIds[po.id]"
                class="shipping-po-panel-footer-control shipping-po-panel-add-sku-list"
              >
                <div
                  v-for="(draft, index) in getAddSkuDrafts(po)"
                  :key="`${po.id}-add-sku-${String(index)}`"
                  class="shipping-po-panel-add-sku-row"
                >
                  <Dropdown
                    :id="scopedId(`${po.id}-add-sku-${String(index)}`)"
                    :model-value="draft"
                    :options="addSkuOptionsFor(po)"
                    placeholder="Select SKU"
                    filterable
                    :disabled="
                      addSkuOptionsFor(po).length === 0 ||
                      saving ||
                      decisionPending
                    "
                    @update:model-value="
                      (value: string | undefined) =>
                        onAddSkuSelected(po, index, value)
                    "
                  />
                  <button
                    v-if="index === getAddSkuDrafts(po).length - 1"
                    type="button"
                    class="shipping-po-panel-icon-button"
                    data-testid="add-sku-picker-button"
                    :aria-label="`Add another SKU picker to PO ${poDisplayNumber(po)}`"
                    :disabled="saving || decisionPending"
                    @click="addSkuPickerRow(po)"
                  >
                    <LucideIcon name="plus" :size="16" />
                  </button>
                </div>
              </div>
              <span v-else class="shipping-po-panel-footer-value">
                Edit PO to add SKUs
              </span>
            </div>
            <div class="shipping-po-panel-footer-row">
              <span class="shipping-po-panel-footer-label">Deliver To</span>
              <div
                v-if="editingPoIds[po.id]"
                class="shipping-po-panel-footer-control"
              >
                <Dropdown
                  :id="scopedId(`${po.id}-deliver-to`)"
                  v-model="po.deliverTo"
                  :options="deliverToOptionsFor(po)"
                  placeholder="Select warehouse"
                  :disabled="saving || decisionPending"
                />
              </div>
              <span
                v-else
                class="shipping-po-panel-footer-value"
                data-testid="deliver-to-value"
                >{{ deliverToLabel(po) }}</span
              >
            </div>
            <div class="shipping-po-panel-footer-row">
              <span class="shipping-po-panel-footer-label">Ship Date</span>
              <div
                v-if="editingPoIds[po.id]"
                class="shipping-po-panel-footer-control"
              >
                <DatePicker
                  v-model="po.shipDateValue"
                  placeholder="MM/DD/YYYY"
                />
              </div>
              <span
                v-else
                class="shipping-po-panel-footer-value"
                data-testid="ship-date-value"
                >{{ formatDateForDisplay(po.shipDateValue) }}</span
              >
            </div>
            <div class="shipping-po-panel-footer-row">
              <span class="shipping-po-panel-footer-label">Arrival Date</span>
              <div
                v-if="editingPoIds[po.id]"
                class="shipping-po-panel-footer-control"
              >
                <DatePicker v-model="po.arrivalDate" placeholder="MM/DD/YYYY" />
              </div>
              <span
                v-else
                class="shipping-po-panel-footer-value"
                data-testid="arrival-date-value"
                >{{ formatDateForDisplay(po.arrivalDate) }}</span
              >
            </div>
          </fieldset>
        </div>
      </div>
    </div>

    <div
      class="shipping-po-panel-section-footer"
      data-testid="shipping-po-section-footer"
    >
      <div
        class="shipping-po-panel-section-footer-field shipping-po-panel-cc-field"
      >
        <span class="shipping-po-panel-footer-label">
          <LucideIcon name="mail" :size="16" />
          cc:
        </span>
        <ul class="shipping-po-panel-cc-list" aria-label="CC email addresses">
          <li
            v-for="email in ccEmails"
            :key="email"
            class="shipping-po-panel-cc-chip"
          >
            {{ email }}
            <button
              type="button"
              class="shipping-po-panel-cc-remove"
              :aria-label="`Remove ${email} from CC`"
              @click="removeCcEmail(email)"
            >
              <LucideIcon name="x" :size="12" />
            </button>
          </li>
        </ul>
        <div class="shipping-po-panel-cc-add">
          <Textbox
            v-model="ccEmailDraft"
            label="Add CC email address"
            placeholder="name@example.com"
            class="shipping-po-panel-cc-input"
            @keydown.enter.prevent="addCcEmail"
          />
          <button
            type="button"
            class="btn btn-secondary"
            data-testid="add-cc-button"
            @click="addCcEmail"
          >
            Add
          </button>
        </div>
        <p
          v-if="ccEmailError"
          class="shipping-po-panel-field-error"
          role="alert"
        >
          {{ ccEmailError }}
        </p>
      </div>
      <label
        class="shipping-po-panel-section-footer-check"
        :for="scopedId('shipping-po-fax-queue')"
      >
        <input
          :id="scopedId('shipping-po-fax-queue')"
          v-model="sendToFaxQueue"
          type="checkbox"
          data-testid="fax-queue-checkbox"
        />
        Send to BluVista Fax Queue
      </label>
      <label
        class="shipping-po-panel-section-footer-check"
        :for="scopedId('shipping-po-email-confirmation')"
      >
        <input
          :id="scopedId('shipping-po-email-confirmation')"
          v-model="requestEmailConfirmation"
          type="checkbox"
          data-testid="email-confirmation-checkbox"
        />
        Request Email Confirmation
      </label>
      <div
        v-if="requestEmailConfirmation"
        class="shipping-po-panel-section-footer-field"
      >
        <FormField
          label="Confirmation Email Address"
          label-placement="left"
          class="shipping-po-confirmation-field"
          required
        >
          <Textbox
            v-model="confirmationEmail"
            label="Confirmation Email Address"
            placeholder="name@example.com"
            class="shipping-po-panel-confirmation-input"
            @blur="validateConfirmationEmail"
          />
          <p
            v-if="confirmationEmailError"
            class="shipping-po-panel-field-error"
            role="alert"
          >
            {{ confirmationEmailError }}
          </p>
        </FormField>
      </div>
    </div>
    <LeftoverInventorySidebar
      :id-prefix="idPrefix"
      :rows="liveLeftoverRows"
      :is-open="leftoverInventoryOpen"
      :modal-open="leftoverMoveModalOpen"
      :move-disabled="Boolean(activeEditId) || decisionPending"
      :selected-skus="leftoverSelectedSkus"
      @close="leftoverInventoryOpen = false"
      @move="openLeftoverMoveModal"
      @selected-skus-change="leftoverSelectedSkus = $event"
    />
  </div>
</template>

<style scoped>
.shipping-po-panel {
  display: flex;
  min-height: 0;
  flex: 1 1 0;
  flex-direction: column;
  gap: 1rem;
}

.shipping-po-panel-rail-layout {
  min-height: auto;
  flex: 0 0 auto;
  gap: 0.75rem;
}

.shipping-po-panel-grid-layout {
  min-width: 0;
  min-height: auto;
  flex: 0 0 auto;
}

.shipping-po-panel-illustrative-note {
  margin: 0;
  border-radius: 8px;
  background: #fff7e6;
  padding: 0.75rem 1rem;
  color: #7a5b00;
  font-size: 0.8rem;
}

.shipping-po-panel-section-header {
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  justify-content: space-between;
  gap: 1.25rem;
  border: 1px solid var(--border-subtle, #d8dee8);
  border-radius: 10px;
  background: var(--surface-primary, #fff);
  padding: 1rem 1.25rem;
}

.shipping-po-panel-context-label {
  display: block;
  color: var(--text-secondary, #64748b);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.shipping-po-panel-section-header-total strong {
  display: block;
  margin-top: 0.15rem;
  color: var(--text-primary, #172033);
  font-size: 1.25rem;
}

.shipping-po-panel-section-header-selects {
  display: flex;
  flex-wrap: wrap;
  align-items: start;
  gap: 1rem;
}

.shipping-po-panel-section-header-selects .form-field-container {
  width: 150px;
  margin: 0;
}

.shipping-po-panel-section-header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.6rem;
}

.shipping-po-panel-more-menu {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
}

.shipping-po-panel-layout-toggle {
  width: 2rem;
  height: 2rem;
}

.shipping-po-panel-layout-toggle[aria-pressed="true"] {
  border-color: var(--action-primary, #1261a0);
  background: #eef4fb;
  color: var(--action-primary, #1261a0);
}

.shipping-po-panel-more-menu-list {
  position: absolute;
  z-index: 30;
  top: calc(100% + 0.3rem);
  right: 0;
  display: flex;
  flex-direction: column;
  min-width: 180px;
  margin: 0;
  border: 1px solid var(--border-subtle, #d8dee8);
  border-radius: 8px;
  background: var(--surface-primary, #fff);
  padding: 0.35rem;
  list-style: none;
  box-shadow: 0 10px 24px rgb(20 33 61 / 14%);
}

.shipping-po-panel-more-menu-list button {
  width: 100%;
  border: none;
  border-radius: 6px;
  background: transparent;
  padding: 0.5rem 0.65rem;
  color: var(--text-primary, #172033);
  font-size: 0.85rem;
  text-align: left;
  cursor: pointer;
}

.shipping-po-panel-more-menu-list button:hover,
.shipping-po-panel-more-menu-list button:focus-visible {
  background: #eef4fb;
  outline: none;
}

.shipping-po-panel-field-error {
  margin: 0.35rem 0 0;
  color: #b60007;
  font-size: 0.78rem;
}

.shipping-po-panel-action-notice {
  margin: 0;
  border-radius: 8px;
  background: #eef4fb;
  padding: 0.65rem 1rem;
  color: var(--action-primary, #1261a0);
  font-size: 0.82rem;
}

.shipping-po-panel-leftover-move-notice {
  margin: 0;
  border-left: 3px solid var(--status-success, #15803d);
  border-radius: 8px;
  background: var(--status-success-surface, #d8f5e4);
  padding: 0.65rem;
  color: var(--status-success, #15803d);
  font-size: 0.85rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.shipping-po-panel-card {
  display: flex;
  min-height: 0;
  flex-direction: column;
  gap: 0.75rem;
  border: 1px solid var(--border-subtle, #d8dee8);
  border-radius: 10px;
  background: var(--surface-primary, #fff);
  padding: 1.25rem;
}

.shipping-po-panel-stack {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.shipping-po-panel-grid {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: 480px;
  align-items: stretch;
  gap: 0.75rem;
}

.shipping-po-panel-grid > .shipping-po-panel-card {
  box-sizing: border-box;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  padding: 0.5rem;
}

@media (width >= 1800px) {
  .shipping-po-panel-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (width >= 2400px) {
  .shipping-po-panel-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

@media (width >= 3000px) {
  .shipping-po-panel-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}

.shipping-po-panel-rail {
  /* Keep absolute screen-reader labels inside this scroller, not the page. */
  position: relative;
  display: flex;
  min-height: auto;
  flex: 0 0 auto;
  align-items: stretch;
  gap: 0.75rem;
  overflow: auto hidden;
  padding-bottom: 0.35rem;
}

.shipping-po-panel-rail-card {
  box-sizing: border-box;
  width: 400px;
  min-width: 400px;
  height: 480px;
  flex: 0 0 400px;
  overflow: hidden;
  padding: 0.5rem;
}

.shipping-po-panel-table-wrap {
  min-height: 0;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: auto;
}

.shipping-po-panel-card-header {
  display: flex;
  position: relative;
  justify-content: space-between;
  padding-right: 4.5rem;
  font-size: 11px;
  color: var(--text-primary, #172033);
}

.shipping-po-panel-card-controls {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  gap: 0.35rem;
}

.shipping-po-panel-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-subtle, #d8dee8);
  border-radius: 6px;
  background: var(--surface-primary, #fff);
  padding: 0.3rem;
  color: var(--text-secondary, #5d6878);
  cursor: pointer;
}

.shipping-po-panel-icon-button:hover,
.shipping-po-panel-icon-button:focus-visible {
  border-color: var(--action-primary, #1261a0);
  color: var(--action-primary, #1261a0);
  outline: none;
}

.shipping-po-panel-layout-toggle:focus-visible {
  outline: 2px solid var(--action-primary, #1261a0);
  outline-offset: 2px;
}

.shipping-po-panel-card-stats {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  color: var(--text-primary, #172033);
  font-size: 0.85rem;
  font-weight: 600;
}

.shipping-po-panel-updated-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: var(--text-secondary, #5d6878);
  font-size: 0.72rem;
  font-weight: 600;
}

.shipping-po-panel-updates-button {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
}

.shipping-po-panel-updates-button:focus-visible {
  outline: 2px solid currentcolor;
  outline-offset: 2px;
}

.shipping-po-panel-utilization {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: var(--text-secondary, #5d6878);
  font-size: 0.8rem;
}

.shipping-po-panel-utilization-track {
  overflow: hidden;
  height: 8px;
  border-radius: 999px;
  background: #eef4fb;
}

.shipping-po-panel-utilization-fill {
  height: 100%;
  border-radius: 999px;
  background: var(--action-primary, #1261a0);
}

.shipping-po-panel-utilization-fill-near-capacity {
  background: var(--status-success, #27ae60);
}

.shipping-po-panel-utilization-fill-over-capacity {
  background: var(--status-critical, #ef4444);
}

.shipping-po-panel-table {
  width: 100%;
  min-width: 0;
  table-layout: fixed;
  border-collapse: collapse;
  color: var(--text-primary, #172033);
  font-size: 0.8rem;
}

.shipping-po-panel-table th,
.shipping-po-panel-table td {
  border-bottom: 1px solid var(--border-subtle, #d8dee8);
  padding: 0.25rem 0.1rem;
  text-align: left;
  vertical-align: middle;
}

.shipping-po-panel-table tbody tr:nth-child(2n + 1) {
  background: rgb(0 0 0 / 3%);
}

.shipping-po-panel-table tbody tr.shipping-po-panel-leftover-move-highlight {
  background: #fff2bf;
}

.shipping-po-panel-sku-column {
  width: 4.25rem;
  overflow-wrap: anywhere;
}

.shipping-po-panel-qty-column {
  width: 3rem;
}

.shipping-po-panel-needed-by-column {
  width: 6.8rem;
}

.shipping-po-panel-price-column {
  width: 5.8rem;
}

.shipping-po-panel-remove-column {
  width: 1.75rem;
  padding-inline: 0.05rem !important;
}

.shipping-po-panel-table th {
  color: var(--text-secondary, #5d6878);
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.shipping-po-panel-table th:not(.shipping-po-panel-remove-column),
.shipping-po-panel-table td:not(.shipping-po-panel-remove-column) {
  padding-inline: 0.1rem;
}

.shipping-po-panel-qty-input {
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  padding-inline: 0.25rem;
  font-size: 0.8rem;
}

.shipping-po-panel-price-input {
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  padding-inline: 0.25rem;
  font-size: 0.8rem;
}

.shipping-po-panel-needed-by-cell {
  min-width: 0;
}

.shipping-po-panel-readonly-value {
  display: block;
  min-height: 1.8rem;
  padding: 0.35rem 0.25rem;
  font-size: 0.8rem;
}

/* Needed By's label is visually redundant with the column header; keep it
   for accessibility only. */
/* stylelint-disable-next-line selector-pseudo-class-no-unknown */
.shipping-po-panel-needed-by-cell :deep(.form-field-label) {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
  padding: 0;
}

/* stylelint-disable-next-line selector-pseudo-class-no-unknown */
.shipping-po-panel-needed-by-cell :deep(.form-field-container) {
  margin: 0;
  width: 100%;
  min-width: 0;
}

/* stylelint-disable-next-line selector-pseudo-class-no-unknown */
.shipping-po-panel-needed-by-cell :deep(.po-date-picker) {
  min-width: 0;
  width: 100%;
}

/* stylelint-disable-next-line selector-pseudo-class-no-unknown */
.shipping-po-panel-needed-by-cell :deep(.po-date-picker .mx-input) {
  min-width: 0;
  padding-inline: 0.35rem 1.35rem;
  font-size: 0.8rem;
}

/* stylelint-disable-next-line selector-pseudo-class-no-unknown */
.shipping-po-panel-needed-by-cell :deep(.po-date-picker .mx-icon-calendar) {
  right: 0.35rem;
}

.shipping-po-panel-card-footer {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
  border-top: 1px solid var(--border-subtle, #d8dee8);
  padding-top: 0.75rem;
}

.shipping-po-panel-footer-row {
  display: grid;
  grid-template-columns: 7.5rem minmax(0, 1fr);
  align-items: center;
  gap: 0.5rem;
}

.shipping-po-panel-footer-label {
  color: var(--text-secondary, #5d6878);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-align: left;
  text-transform: uppercase;
}

.shipping-po-panel-footer-value {
  font-size: 0.8rem;
}

.shipping-po-panel-footer-control {
  min-width: 0;
}

.shipping-po-panel-add-sku-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.shipping-po-panel-add-sku-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.shipping-po-panel-add-sku-row > :first-child {
  min-width: 0;
  flex: 1 1 auto;
}

.shipping-po-panel-section-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem 1.5rem;
  margin-top: 0.75rem;
  border-top: 1px solid var(--border-subtle, #d8dee8);
  padding-top: 0.75rem;
}

.shipping-po-panel-section-footer-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.35rem;
}

.shipping-po-panel-cc-field {
  align-items: center;
  flex-flow: row wrap;
}

.shipping-po-panel-cc-field .shipping-po-panel-footer-label {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.25rem;
  text-transform: none;
}

.shipping-po-panel-cc-field > .shipping-po-panel-field-error {
  flex-basis: 100%;
}

.shipping-po-panel-section-footer-check {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 2.6rem;
  font-size: 0.85rem;
}

.shipping-po-panel-cc-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.shipping-po-panel-cc-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border: 1px solid var(--border-subtle, #d8dee8);
  border-radius: 999px;
  padding: 0.15rem 0.35rem 0.15rem 0.6rem;
  font-size: 0.78rem;
}

.shipping-po-panel-cc-remove {
  display: inline-flex;
  align-items: center;
  border: 0;
  border-radius: 999px;
  background: transparent;
  padding: 0.1rem;
  color: inherit;
  cursor: pointer;
}

.shipping-po-panel-cc-add {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.shipping-po-panel-cc-add > .btn {
  padding-inline: 0.5rem;
}

.shipping-po-panel-cc-input,
.shipping-po-panel-confirmation-input {
  width: 15rem;
  max-width: 100%;
}

/* stylelint-disable-next-line selector-pseudo-class-no-unknown */
.shipping-po-confirmation-field :deep(.form-field-label) {
  text-transform: none;
  font-size: 1rem;
}

@media (width <= 850px) {
  .shipping-po-panel-rail {
    min-height: 15rem;
    flex: 0 0 15rem;
  }

  .shipping-po-panel-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
